/**
 * LOCKED_FIELDS — the canonical list of wizard fields whose values are
 * governed by org-level profiles rather than per-analysis analyst input.
 *
 * See ADR-029 (Input Governance). Keys are dotted wizard paths
 * (`step{N}.fieldName`) so both the UI (read-only rendering) and the API
 * (submission validator) can consume the same source of truth.
 *
 * Any change to this list requires an ADR-029 amendment.
 *
 * Field → source profile mapping:
 *   - Step 5 growth/depreciation rates → assumption_profile
 *   - Step 6 tax rates + cost-of-services → jurisdiction_profile
 *   - Step 7 multipliers + capture rates + displacement → assumption_profile
 *   - Step 8 discountRate → assumption_profile
 */

export type LockedField = {
  /** Dotted wizard path, e.g. `step6.salesTaxRate`. */
  path: string;
  /** Which profile holds the authoritative value. */
  profile: 'jurisdiction' | 'assumption';
  /** Column name on the profile row. */
  profileColumn: string;
};

export const LOCKED_FIELDS: readonly LockedField[] = [
  // ----- Step 5 (assumption_profile) -----
  {
    path: 'step5.realPropertyAppreciationRate',
    profile: 'assumption',
    profileColumn: 'realPropertyAppreciationRate',
  },
  {
    path: 'step5.bppDepreciationRate',
    profile: 'assumption',
    profileColumn: 'bppDepreciationRate',
  },
  {
    path: 'step5.inventoryGrowthRate',
    profile: 'assumption',
    profileColumn: 'inventoryGrowthRate',
  },

  // ----- Step 6 (jurisdiction_profile) -----
  { path: 'step6.salesTaxRate', profile: 'jurisdiction', profileColumn: 'salesTaxRate' },
  { path: 'step6.propertyTaxRate', profile: 'jurisdiction', profileColumn: 'propertyTaxRate' },
  {
    path: 'step6.hotelOccupancyTaxRate',
    profile: 'jurisdiction',
    profileColumn: 'hotelOccupancyTaxRate',
  },
  {
    path: 'step6.mixedBeverageShareRate',
    profile: 'jurisdiction',
    profileColumn: 'mixedBeverageShareRate',
  },
  {
    path: 'step6.countyPropertyTaxRate',
    profile: 'jurisdiction',
    profileColumn: 'countyPropertyTaxRate',
  },
  {
    path: 'step6.isdPropertyTaxRate',
    profile: 'jurisdiction',
    profileColumn: 'isdPropertyTaxRate',
  },
  {
    path: 'step6.specialDistrictPropertyTaxRate',
    profile: 'jurisdiction',
    profileColumn: 'specialDistrictPropertyTaxRate',
  },
  {
    path: 'step6.miscTaxesFeesPerWorker',
    profile: 'jurisdiction',
    profileColumn: 'miscTaxesFeesPerWorker',
  },
  {
    path: 'step6.householdTaxableSpendingPerWorker',
    profile: 'jurisdiction',
    profileColumn: 'householdTaxableSpendingPerWorker',
  },
  {
    path: 'step6.utilityFranchiseFeePerJob',
    profile: 'jurisdiction',
    profileColumn: 'utilityFranchiseFeePerJob',
  },
  {
    path: 'step6.baseCityCostPerWorker',
    profile: 'jurisdiction',
    profileColumn: 'baseCityCostPerWorker',
  },
  {
    path: 'step6.baseCountyCostPerWorker',
    profile: 'jurisdiction',
    profileColumn: 'baseCountyCostPerWorker',
  },
  {
    path: 'step6.baseIsdCostPerStudent',
    profile: 'jurisdiction',
    profileColumn: 'baseIsdCostPerStudent',
  },
  {
    path: 'step6.specialDistrictCostPerWorker',
    profile: 'jurisdiction',
    profileColumn: 'specialDistrictCostPerWorker',
  },
  { path: 'step6.costInflationRate', profile: 'jurisdiction', profileColumn: 'costInflationRate' },

  // ----- Step 7 (assumption_profile) -----
  {
    path: 'step7.operationsEmploymentMultiplier',
    profile: 'assumption',
    profileColumn: 'operationsEmploymentMultiplier',
  },
  {
    path: 'step7.operationsEarningsMultiplier',
    profile: 'assumption',
    profileColumn: 'operationsEarningsMultiplier',
  },
  {
    path: 'step7.constructionEmploymentMultiplierJobsPerMillion',
    profile: 'assumption',
    profileColumn: 'constructionEmploymentMultiplierJobsPerMillion',
  },
  {
    path: 'step7.constructionEarningsMultiplier',
    profile: 'assumption',
    profileColumn: 'constructionEarningsMultiplier',
  },
  {
    path: 'step7.constructionOutputMultiplier',
    profile: 'assumption',
    profileColumn: 'constructionOutputMultiplier',
  },
  {
    path: 'step7.localConstructionCaptureRate',
    profile: 'assumption',
    profileColumn: 'localConstructionCaptureRate',
  },
  {
    path: 'step7.workerLocalCaptureRate',
    profile: 'assumption',
    profileColumn: 'workerLocalCaptureRate',
  },
  {
    path: 'step7.workerTaxableSpendingShare',
    profile: 'assumption',
    profileColumn: 'workerTaxableSpendingShare',
  },
  {
    path: 'step7.materialsShareOfConstruction',
    profile: 'assumption',
    profileColumn: 'materialsShareOfConstruction',
  },
  {
    path: 'step7.materialsTaxabilityRate',
    profile: 'assumption',
    profileColumn: 'materialsTaxabilityRate',
  },
  { path: 'step7.permitFeeRate', profile: 'assumption', profileColumn: 'permitFeeRate' },
  { path: 'step7.displacementRate', profile: 'assumption', profileColumn: 'displacementRate' },
  {
    path: 'step7.workerRelocationPct',
    profile: 'assumption',
    profileColumn: 'workerRelocationPct',
  },
  {
    path: 'step7.averageHouseholdSize',
    profile: 'assumption',
    profileColumn: 'averageHouseholdSize',
  },
  {
    path: 'step7.schoolAgeChildrenPerHousehold',
    profile: 'assumption',
    profileColumn: 'schoolAgeChildrenPerHousehold',
  },

  // ----- Step 3 (assumption_profile) -----
  { path: 'step3.payrollGrowthRate', profile: 'assumption', profileColumn: 'payrollGrowthRate' },

  // ----- Step 4 (assumption_profile) -----
  { path: 'step4.salesGrowthRate', profile: 'assumption', profileColumn: 'salesGrowthRate' },

  // ----- Step 8 (assumption_profile) -----
  { path: 'step8.discountRate', profile: 'assumption', profileColumn: 'discountRate' },

  // ----- ADR-031 retention counterfactual (assumption_profile) -----
  // Reads from `projectTypeOverrides.retention.defaultProbabilityOfDeparture`
  // — only meaningful when `step1.projectType === 'retention'`. The
  // governance check skips this field for non-retention submissions.
  {
    path: 'retentionCounterfactual.probabilityOfDeparture',
    profile: 'assumption',
    profileColumn: 'defaultProbabilityOfDeparture',
  },
] as const;

/** Fast path set for O(1) "is this path locked?" lookups. */
export const LOCKED_FIELD_PATHS: ReadonlySet<string> = new Set(LOCKED_FIELDS.map((f) => f.path));

export function isLockedField(path: string): boolean {
  return LOCKED_FIELD_PATHS.has(path);
}

/** Paths governed by the assumption_profile (Step 5, 7, 8). */
export const ASSUMPTION_PROFILE_FIELDS = LOCKED_FIELDS.filter((f) => f.profile === 'assumption');

/** Paths governed by the jurisdiction_profile (Step 6). */
export const JURISDICTION_PROFILE_FIELDS = LOCKED_FIELDS.filter(
  (f) => f.profile === 'jurisdiction',
);
